import { HttpParams } from "@angular/common/http";
import { NXTRIAL_IDENTIFIER, MERILLIFE_IDENTIFIER, STRING } from "./constant";

export const isEmptyString = (value: string | null | undefined): boolean =>
  value == null || (typeof value === 'string' && value.trim().length === 0);

export const isNotEmptyString = (value: string | null | undefined): boolean =>
  !isEmptyString(value);

export function getIconPathByHostname(nxtrialPath: string, merillifePath: string, defaultPath: string): string {
  const currentUrl = window.location.hostname;
  
  if (currentUrl.includes(NXTRIAL_IDENTIFIER)) {
    return nxtrialPath;
  } else if (currentUrl.includes(MERILLIFE_IDENTIFIER)) {
    return merillifePath;
  }
  
  return defaultPath;
}

export function preventRefresh(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = '';
}

type ParamValue = string | number | boolean | string[] | number[] | boolean[];

export function buildHttpParams<T extends object>(paramsObj: Partial<T>): HttpParams {
  let params = new HttpParams();

  (Object.keys(paramsObj) as (keyof T)[]).forEach((key) => {
    const value = paramsObj[key] as ParamValue | undefined | null;

    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      if (value.length) {
        params = params.set(String(key), value.join(','));
      }
    } else {
      params = params.set(String(key), String(value));
    }
  });

  return params;
}




// ---- Objects (java.util.Objects) ----
const Objects = {
  isNull: (v: unknown): v is null | undefined => v === null || v === undefined,
  nonNull: (v: unknown): boolean => v !== null && v !== undefined,
};

// ---- StringUtils (commons-lang3) ----
const StringUtils = {
  isBlank: (s: string | null | undefined): boolean => s == null || s.trim().length === 0,
  isNotBlank: (s: string | null | undefined): boolean => !StringUtils.isBlank(s),
};

// ---- CollectionUtils (Collection, except Map): Array and Set ----
type Collection = unknown[] | Set<unknown>;

const CollectionUtils = {
  isEmpty: (c: Collection | null | undefined): boolean =>
    c == null || (Array.isArray(c) ? c.length : c.size) === 0,
  isNotEmpty: (c: Collection | null | undefined): boolean => !CollectionUtils.isEmpty(c),
};

// ---- MapUtils ----
const MapUtils = {
  isEmpty: (m: Map<unknown, unknown> | null | undefined): boolean => m == null || m.size === 0,
  isNotEmpty: (m: Map<unknown, unknown> | null | undefined): boolean => !MapUtils.isEmpty(m),
};

// ---------------------------------------------------------------

export function cleanRequestDto<T>(value: T): T {
  const cleaned = clean(value);
  if (Objects.isNull(cleaned)) {
    // Top level is never removed; return an empty container of the same kind
    return (Array.isArray(value) ? [] : {}) as T;
  }
  return cleaned as T;
}

/** Returns undefined when the value is "empty", so the caller drops it. */
function clean(value: unknown): unknown {
  // Objects.isNull
  if (Objects.isNull(value)) return undefined;

  // StringUtils.isBlank
  if (typeof value === 'string') {
    return StringUtils.isBlank(value) ? undefined : value;
  }

  // number, boolean, bigint... (0 and false are kept)
  if (typeof value !== 'object') return value;

  // CollectionUtils: Array and Set (Set is sent as an array)
  if (Array.isArray(value) || value instanceof Set) {
    const out: unknown[] = [];
    for (const item of value) {
      const c = clean(item);
      if (Objects.nonNull(c)) out.push(c);
    }
    return CollectionUtils.isNotEmpty(out) ? out : undefined;
  }

  // MapUtils: Map (sent as a plain object, same as Jackson serializes a Map)
  if (value instanceof Map) {
    const out = new Map<unknown, unknown>();
    for (const [k, v] of value) {
      const c = clean(v);
      if (Objects.nonNull(c)) out.set(k, c);
    }
    return MapUtils.isNotEmpty(out) ? Object.fromEntries(out) : undefined;
  }

  // Date, File, Blob, class instances: leave untouched
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;

  // Plain object: drop empty keys, drop the object itself if nothing is left
  const src = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  let hasKeys = false;
  for (const key of Object.keys(src)) {
    const c = clean(src[key]);
    if (Objects.nonNull(c)) {
      out[key] = c;
      hasKeys = true;
    }
  }
  return hasKeys ? out : undefined;
}


export function concatString(...args: any[]): string {
  if (args.length === 1 && typeof args[0] === STRING) {
    return args[0];
  }
  return args .map((arg) => {
      if (arg === undefined || arg === null) return ''; // handles null & undefined
      return String(arg);
    }).join('');
}